import { Button, Card, Container, Row, Col, Badge } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import MaterialReactTable from "material-react-table";
import { toast, ToastContainer } from "react-toastify";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  deleteSaleInvoice,
  fetchSaleInvoices,
  addSaleInvoice,
  updateInvoiceStatus,
} from "../../../Redux/saleInvoiceSlice";

function ListSaleInvoice() {
  const notify = (type, msg) => {
    if (type === 1)
      toast.success(
        <strong>
          <i className="fas fa-check-circle"></i>
          {msg}
        </strong>,
      );
    else
      toast.error(
        <strong>
          <i className="fas fa-exclamation-circle"></i>
          {msg}
        </strong>,
      );
  };

  const [alert, setAlert] = useState(null);
  const [closeAlert, setCloseAlert] = useState(null);
  const navigate = useHistory();
  const location = useLocation();

  // Get the last path segment
  const type = location.pathname.split("/").pop();
  const dispatch = useDispatch();
  const [invoices, setInvoices] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  // Function to get the correct add route based on invoice type
  const getAddRoute = (invoiceType) => {
    switch (invoiceType) {
      case "DELIVERY_NOTE":
        return "/delivery-note/add";
      case "SALE_INVOICE":
        return "/sale-invoice/add";
      case "SHIPPING_NOTE_INVOICE":
        return "/shipping-note/add";
      case "QUOTATION":
        return "/sale-invoice/add";
      case "SALE_REFUND":
        return "/refund-invoice/add";
      default:
        return "/sale-invoice/add";
    }
  };

  // Function to get the correct update route based on invoice type
  const getUpdateRoute = (invoice) => {
    switch (invoice.type) {
      case "DELIVERY_NOTE":
        return `/delivery-note/update/${invoice.id}`;
      case "SALE_INVOICE":
        return `/sale-invoice/update/${invoice.id}`;
      case "SHIPPING_NOTE_INVOICE":
        return `/shipping-note/update/${invoice.id}`;
      case "QUOTATION":
        return `/sale-invoice/update/${invoice.id}`;
      case "SALE_REFUND":
        return `/refund-invoice/update/${invoice.id}`;
      default:
        return `/sale-invoice/update/${invoice.id}`;
    }
  };

  // Function to get the correct button label based on invoice type
  const getButtonLabel = (invoiceType) => {
    switch (invoiceType) {
      case "DELIVERY_NOTE":
        return "Nouveau bon de livraison";
      case "SALE_INVOICE":
        return "Nouvelle facture de vente";
      case "SHIPPING_NOTE_INVOICE":
        return "Nouvelle facture bon sortie";
      case "QUOTATION":
        return "Nouveau devis";
      case "SALE_REFUND":
        return "Nouvel avoir client";
      default:
        return "Nouvelle facture de vente";
    }
  };

  // Function to get page title based on invoice type
  const getPageTitle = (invoiceType) => {
    switch (invoiceType) {
      case "DELIVERY_NOTE":
        return "Bons de livraison";
      case "SALE_INVOICE":
        return "Factures de vente";
      case "SHIPPING_NOTE_INVOICE":
        return "Factures bon sortie";
      case "QUOTATION":
        return "Devis";
      case "SALE_REFUND":
        return "Avoirs client";
      default:
        return "Factures de vente";
    }
  };

  const columns = [
    {
      header: "Numéro",
      accessorKey: "invoiceNumber",
    },
    {
      header: "Date",
      accessorKey: "date",
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
    },
    {
      header: "Client",
      accessorKey: "client.name",
    },
    {
      header: "Chauffeur",
      accessorKey: "driver",
      Cell: ({ cell }) => {
        const driver = cell.getValue();
        return driver ? `${driver.firstName} ${driver.lastName}` : "-";
      },
    },
    {
      header: "CIN Chauffeur",
      accessorKey: "driver.cin",
      Cell: ({ cell }) => {
        const cin = cell.getValue();
        return cin || "-";
      },
    },
    {
      header: "Type",
      accessorKey: "type",
      Cell: ({ cell }) => {
        const type = cell.getValue();
        const typeLabels = {
          QUOTATION: "Devis",
          DELIVERY_NOTE: "Bon de livraison",
          SALE_INVOICE: "Facture vente",
          SALE_REFUND: "Avoir client",
          SHIPPING_NOTE_INVOICE: "Facture bon sortie",
        };
        return <Badge bg="info">{typeLabels[type] || type}</Badge>;
      },
    },
    {
      header: "Statut",
      accessorKey: "status",
      Cell: ({ cell }) => {
        const status = cell.getValue();
        const variant =
          {
            DRAFT: "warning",
            VALIDATED: "primary",
            PAID: "success",
            CANCELLED: "danger",
            CLOSED: "secondary",
          }[status] || "secondary";

        return (
          <Badge bg={variant}>
            {status === "DRAFT"
              ? "Brouillon"
              : status === "VALIDATED"
                ? "Validée"
                : status === "PAID"
                  ? "Payée"
                  : status === "CLOSED"
                    ? "Clôturée"
                    : "Annulée"}
          </Badge>
        );
      },
    },
    {
      header: "Total HT (DT)",
      accessorKey: "totalHT",
      Cell: ({ cell }) => parseFloat(cell.getValue()).toFixed(2),
    },
    {
      header: "Total TTC (DT)",
      accessorKey: "totalTTC",
      Cell: ({ cell }) => parseFloat(cell.getValue()).toFixed(2),
    },
    {
      header: "Actions",
      accessorKey: "id",
      Cell: ({ cell, row }) => {
        const invoice = row.original;
        const isShippingNoteInvoice = invoice.type === "SHIPPING_NOTE_INVOICE";
        const isCommercial = currentUser?.role === "COMMERCIAL";

        // If user is COMMERCIAL, show ONLY the view button
        if (isCommercial && isShippingNoteInvoice) {
          return (
            <div className="actions-right">
              <Button
                onClick={() =>
                  navigate.push("/sale-invoice/detail/" + cell.getValue())
                }
                variant="info"
                size="sm"
                className="text-info btn-link"
                title="Voir détails"
              >
                <i className="fa fa-eye" />
              </Button>
            </div>
          );
        }

        // For non-COMMERCIAL users, show all buttons as before
        return (
          <div className="actions-right">
            {/* Duplicate button - shown for all invoices */}
            {isShippingNoteInvoice && (
              <Button
                onClick={() => handleDuplicate(invoice)}
                variant="primary"
                size="sm"
                className="text-primary btn-link duplicate mr-1"
                title="Dupliquer"
              >
                <i className="fa fa-copy" />
              </Button>
            )}

            {/* Close button - only for SHIPPING_NOTE_INVOICE and not already closed */}
            {isShippingNoteInvoice &&
              invoice.status !== "CLOSED" &&
              invoice.status !== "CANCELLED" && (
                <Button
                  onClick={() => confirmClose(invoice.id)}
                  variant="secondary"
                  size="sm"
                  className="text-secondary btn-link mr-1"
                  title="Clôturer"
                >
                  <i className="fa fa-lock" />
                </Button>
              )}

            {/* Show update button only if status is DRAFT */}
            {invoice.status === "DRAFT" && (
              <Button
                onClick={() => navigate.push(getUpdateRoute(invoice))}
                variant="warning"
                size="sm"
                className="text-warning btn-link edit mr-1"
                title="Modifier"
              >
                <i className="fa fa-edit" />
              </Button>
            )}

            {/* Show delete button only if status is DRAFT */}
            {invoice.status === "DRAFT" && (
              <Button
                onClick={(e) => confirmDelete(cell.getValue(), e)}
                variant="danger"
                size="sm"
                className="text-danger btn-link delete mr-1"
                title="Supprimer"
              >
                <i className="fa fa-trash" />
              </Button>
            )}

            {/* View button always visible for all statuses */}
            <Button
              onClick={() =>
                navigate.push("/sale-invoice/detail/" + cell.getValue())
              }
              variant="info"
              size="sm"
              className="text-info btn-link"
              title="Voir détails"
            >
              <i className="fa fa-eye" />
            </Button>
          </div>
        );
      },
    },
  ];

  const fetchInvoices = async () => {
    try {
      let filters = { type: type };
      console.log(currentUser);

      // If user is COMMERCIAL, filter by driver's CIN
      if (currentUser && currentUser.role === "COMMERCIAL") {
        // Check if user has driverCIN from the JWT/user object
        if (currentUser.cin) {
          filters.driverCIN = currentUser.cin;
        }
      }

      const response = await dispatch(fetchSaleInvoices(filters));
      if (response.payload) {
        setInvoices(response.payload);
      }
    } catch (error) {
      notify(2, "Erreur lors du chargement des factures");
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteSaleInvoice(id))
      .unwrap()
      .then((response) => {
        console.log(response);

        if (response) {
          notify(1, "Facture supprimée avec succès");
          fetchInvoices();
        }
      })
      .catch((error) => {
        notify(2, "Erreur lors de la suppression");
      })
      .finally(() => {
        hideAlert();
      });
  };

  const handleDuplicate = async (invoice) => {
    try {
      // Clean the items array by removing unwanted properties
      const cleanedItems = invoice.items.map((item) => ({
        productId: item.productId || item.product?.id,
        quantity: item.quantity,
        price: item.unitPrice || item.price,
        vatRate: item.vatRate || item.tax?.rate || 0,
        vatAmount: item.vatAmount || 0,
      }));

      const cleanedCityIds =
        invoice.cities?.map((city) => {
          if (typeof city === "object" && city !== null) {
            return city.city.id;
          }
          return city;
        }) || [];

      // Create a duplicate invoice object with cleaned data
      const duplicatedInvoice = {
        invoiceNumber: invoice.invoiceNumber,
        date: new Date().toISOString().split("T")[0],
        startDate: invoice.startDate,
        endDate: invoice.endDate,
        type: "SHIPPING_NOTE_INVOICE",
        status: "DRAFT",
        items: cleanedItems,
        totalHT: invoice.totalHT,
        totalTTC: invoice.totalTTC,
        driverId: invoice.driverId,
        cityIds: cleanedCityIds,
        clientId: invoice.clientId || invoice.client?.id,
        notes: invoice.notes,
      };

      // Remove any undefined or null values
      Object.keys(duplicatedInvoice).forEach(
        (key) =>
          duplicatedInvoice[key] === undefined && delete duplicatedInvoice[key],
      );

      // Save the duplicated invoice
      const response = await dispatch(
        addSaleInvoice(duplicatedInvoice),
      ).unwrap();

      if (response) {
        notify(1, "Facture dupliquée avec succès");
        fetchInvoices();
        navigate.push(`/shipping-note/update/${response.id}`);
      }
    } catch (error) {
      console.error("Duplicate error:", error);
      notify(2, "Erreur lors de la duplication: " + (error.message || ""));
    }
  };

  const handleClose = (id) => {
    dispatch(updateInvoiceStatus({ id, status: "CLOSED" }))
      .unwrap()
      .then((response) => {
        if (response) {
          notify(1, "Facture bon sortie clôturée avec succès");
          fetchInvoices();
        }
      })
      .catch((error) => {
        notify(2, "Erreur lors de la clôture de la facture");
        console.error("Close error:", error);
      })
      .finally(() => {
        hideCloseAlert();
      });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const hideCloseAlert = () => {
    setCloseAlert(null);
  };

  const confirmDelete = (id, e) => {
    setAlert(
      <SweetAlert
        style={{ display: "block", marginTop: "-100px" }}
        title="Êtes-vous sûr de vouloir supprimer cette facture?"
        onConfirm={() => handleDelete(id)}
        onCancel={hideAlert}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="secondary"
        confirmBtnText="Oui, supprimer"
        cancelBtnText="Annuler"
        showCancel
      />,
    );
  };

  const confirmClose = (id) => {
    setCloseAlert(
      <SweetAlert
        style={{ display: "block", marginTop: "-100px" }}
        title="Êtes-vous sûr de vouloir clôturer cette facture bon sortie?"
        onConfirm={() => handleClose(id)}
        onCancel={hideCloseAlert}
        confirmBtnBsStyle="primary"
        cancelBtnBsStyle="secondary"
        confirmBtnText="Oui, clôturer"
        cancelBtnText="Annuler"
        showCancel
      >
        <p className="text-muted">
          Une fois clôturée, cette facture ne pourra plus être modifiée.
        </p>
      </SweetAlert>,
    );
  };

  useEffect(() => {
    if (currentUser !== null) {
      fetchInvoices();
    }
  }, [type, currentUser]);

  return (
    <Container fluid>
      <ToastContainer />
      {alert}
      {closeAlert}
      <Row>
        {/* Show Add button for ADMIN users always, and for COMMERCIAL users only on DELIVERY_NOTE page */}
        {(currentUser?.role === "ADMIN" ||
          (currentUser?.role === "COMMERCIAL" && type === "DELIVERY_NOTE")) && (
          <Col md="12" className="mb-3">
            <Button
              className="btn-wd mr-1"
              type="button"
              variant="success"
              onClick={() => navigate.push(getAddRoute(type))}
            >
              <span className="btn-label">
                <i className="fas fa-plus"></i>
              </span>
              {getButtonLabel(type)}
            </Button>
          </Col>
        )}
        <Col md="12">
          <h4 className="title">{getPageTitle(type)}</h4>
          <Card>
            <Card.Body>
              <MaterialReactTable
                columns={columns}
                data={invoices}
                enableColumnActions
                enableColumnFilters
                enablePagination
                enableSorting
                enableBottomToolbar
                enableTopToolbar
                muiTableBodyRowProps={{ hover: false }}
                localization={{
                  noRecordsToDisplay: "Aucune facture trouvée",
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ListSaleInvoice;
